import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Headphones, User, Play } from "lucide-react";
import AudioPlayer from "@/components/AudioPlayer";
import SEOHead from "@/components/SEOHead";
import api from "@/lib/api";

export default function PodcastPage() {
  const { id } = useParams();
  const [podcast, setPodcast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playingEp, setPlayingEp] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/podcasts/${id}`);
        setPodcast(res.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Skeleton className="h-48 w-full mb-8" />
        <Skeleton className="h-6 w-64 mb-4" />
        {[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full mb-4" />)}
      </div>
    );
  }

  if (!podcast) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-24 text-center">
        <h1 className="text-3xl font-serif text-foreground">Podcast not found</h1>
        <Link to="/" className="text-[#1E88E5] font-sans text-sm mt-4 block">Return to home</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12" data-testid="podcast-page">
      <SEOHead title={podcast.title} description={podcast.description} image={podcast.cover_image} />
      <Link to="/" className="inline-flex items-center gap-2 text-sm font-sans text-muted-foreground hover:text-foreground mb-6" data-testid="back-home">
        <ArrowLeft className="h-4 w-4" /> Back to Home
      </Link>

      {/* Podcast header */}
      <div className="flex flex-col sm:flex-row gap-6 mb-10">
        <img src={podcast.cover_image} alt={podcast.title} className="w-48 h-48 object-cover border border-slate-200 dark:border-slate-700 shrink-0" />
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Headphones className="h-4 w-4 text-[#1E88E5]" />
            <span className="text-[10px] font-sans font-bold uppercase tracking-[0.2em] text-[#1E88E5]">Podcast</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-serif font-bold text-foreground">{podcast.title}</h1>
          <p className="text-sm font-sans text-[#1E88E5] mt-2 flex items-center gap-1">
            <User className="h-3 w-3" /> Hosted by {podcast.host_name}
          </p>
          <p className="text-sm font-sans text-muted-foreground mt-3 leading-relaxed">{podcast.description}</p>
          <p className="text-xs font-sans text-muted-foreground mt-3">{podcast.episodes?.length || 0} episodes</p>
        </div>
      </div>

      {/* Episodes */}
      <h2 className="text-2xl font-serif font-bold text-foreground mb-6">All Episodes</h2>
      <div className="space-y-4">
        {(podcast.episodes || []).map((ep) => (
          <div key={ep.id} className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6" data-testid={`podcast-episode-${ep.id}`}>
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <span className="text-xs font-sans text-muted-foreground">Episode {ep.episode_number}</span>
                <h3 className="font-serif font-bold text-lg text-foreground mt-1">{ep.title}</h3>
                <p className="text-sm font-sans text-muted-foreground mt-1">{ep.description}</p>
              </div>
              <span className="text-sm font-sans text-muted-foreground shrink-0">{ep.duration}</span>
            </div>
            {playingEp === ep.id ? (
              <AudioPlayer src={ep.audio_url} title={ep.title} onEnded={() => setPlayingEp(null)} />
            ) : (
              <button
                onClick={() => setPlayingEp(ep.id)}
                className="flex items-center gap-2 text-sm font-sans text-[#1E88E5] hover:underline"
                data-testid={`podcast-play-${ep.id}`}
              >
                <Play className="h-4 w-4" /> Play Episode
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
